import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listProjects, createProject, updateProject, deleteProject, listClients, listTemplates, getCompany } from "../lib/api";
import useCollapseMap from "../lib/useCollapseMap";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Pencil, Trash2, MapPin, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import OverviewControls from "../components/overview/OverviewControls";
import { useOverviewPreferences } from "../hooks/useOverviewPreferences";
import { applySort, naturalCompare, statusOrderComparator } from "../lib/sortOverview";
import OpenProjectButton from "../components/projects/OpenProjectButton";

// v14ag — Uitgebreide sorteeropties voor Projectenoverzicht (Fase 2).
// Volgorde volgt de gebruikersintentie: eerst tekstuele sorts (klant/project/plaats),
// dan datum-gebaseerd, dan workflow/BTW.
const PROJECT_SORT_OPTIONS = [
  { value: "client_asc", label: "Klant A → Z" },
  { value: "client_desc", label: "Klant Z → A" },
  { value: "name_asc", label: "Projectnaam A → Z" },
  { value: "name_desc", label: "Projectnaam Z → A" },
  { value: "location_asc", label: "Plaats A → Z" },
  { value: "location_desc", label: "Plaats Z → A" },
  { value: "created_desc", label: "Datum aangemaakt" },
  { value: "updated_desc", label: "Laatst gewijzigd" },
  { value: "status_asc", label: "Status (workflow)" },
  { value: "vat_asc", label: "BTW-tarief" },
];
// v14ak — Workflow-order (Concept → Afwachting → Actief → Opgeleverd, Afgewezen als zijpad).
const PROJECT_STATUS_ORDER = ["concept", "waiting", "active", "delivered", "rejected"];

const today = () => new Date().toISOString().slice(0, 10);
const empty = {
  client_id: "", name: "", location: "", worksite_address: "",
  notes: "", date: today(), status: "concept", status_manual_override: false,
  quote_status: "draft", default_vat_rate: 21, project_type: "mixed", template_id: null,
};

// v6 F13 — "Vrij" is verwijderd. Projecttype is nu enkel een administratief
// label (voor overzicht, filters, badges) en beperkt géén functionele opties:
// Ruimte-toevoegen én Werkzone-toevoegen blijven bij elk type beschikbaar.
const PROJECT_TYPES = [
  { key: "interior", label: "Binnen",  hint: "Vooral kamers en binnenschilderwerk" },
  { key: "exterior", label: "Buiten",  hint: "Vooral gevel en buitenhoutwerk" },
  { key: "mixed",    label: "Gemengd", hint: "Binnen én buiten samen (standaard)" },
];

const QUOTE_STATUS_OPTS = [
  { value: "draft", label: "Concept" },
  { value: "sent", label: "Verstuurd" },
  { value: "approved", label: "Goedgekeurd" },
  { value: "rejected", label: "Afgewezen" },
  { value: "invoiced", label: "Afgerond" },
];

// v14ak — 5 workflow-projectstatussen met DS v1-kleuren.
const STATUS_BADGE = {
  concept:   "bg-[#D4A373] text-white",  // warm sand — nieuw / draft
  waiting:   "bg-[#8B7355] text-white",  // muted tan — afwachting van klantbeslissing
  active:    "bg-[#D96C4A] text-white",  // primary terracotta — in uitvoering
  rejected:  "bg-[#B24A2E] text-white",  // deep coral — afgewezen
  delivered: "bg-[#8A9A86] text-white",  // sage — opgeleverd
};
const STATUS_LABEL = {
  concept: "Concept",
  waiting: "Afwachting",
  active: "Actief",
  rejected: "Afgewezen",
  delivered: "Opgeleverd",
};

// v6 C3 — project_type badge config (reused across Projects + Quotes feed)
// v6 F13 — "free" wordt bij DB-migratie omgezet naar "mixed"; badge blijft
// als fallback aanwezig zodat oude records tijdens de eerste boot niet stuk gaan.
export const PROJECT_TYPE_BADGE = {
  interior: { label: "Binnen",  cls: "bg-[#FFF8F2] text-[#D96C4A] border border-[#D96C4A]" },
  exterior: { label: "Buiten",  cls: "bg-[#E8F0E5] text-[#6B8E6B] border border-[#6B8E6B]" },
  mixed:    { label: "Gemengd", cls: "bg-[#F0EBF7] text-[#8A6BA0] border border-[#8A6BA0]" },
  free:     { label: "Gemengd", cls: "bg-[#F0EBF7] text-[#8A6BA0] border border-[#8A6BA0]" },
};

export function ProjectTypeBadge({ type, className = "" }) {
  const cfg = PROJECT_TYPE_BADGE[type] || PROJECT_TYPE_BADGE.mixed;
  return (
    <span
      className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-md tracking-wide uppercase ${cfg.cls} ${className}`}
      data-testid={`project-type-badge-${type || "mixed"}`}
    >
      {cfg.label}
    </span>
  );
}

// v14ag — Compacte BTW-badge voor de Projecten-lijst.
// Uitsluitend gebaseerd op project.default_vat_rate (single source of truth).
// BTW verlegd blijft een factuurregime en wordt hier bewust NIET afgeleid.
function VatBadge({ rate }) {
  const value = rate == null ? null : Number(rate);
  const label = value == null ? "—" : `${value}%`;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[#F0EFEA] text-[#4F4F4D] border border-[#E5E2DC]"
      data-testid={`project-vat-${value ?? "empty"}`}
    >
      {label}
    </span>
  );
}

// v14ag — Datumformattering voor projectlijst: "dd/mm/yy" (nl-BE, compact).
// Fallback naar rauwe string als parsing faalt.
function formatDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

// v14ag — Één rij in de Projecten-lijst.
// Desktop (md+): 7-koloms grid met vaste kolombreedtes en truncate per cel.
// Mobile: compacte kaartlay-out met alle kerninfo + full-width open-actie.
function ProjectListRow({ project: p, clientName, onEdit, onDelete }) {
  return (
    <li className="hover:bg-[#FAF9F6]" data-testid={`project-row-${p.id}`}>
      {/* Desktop grid row */}
      <div
        className="hidden md:grid gap-3 px-4 py-3 items-center text-sm"
        style={{ gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1.4fr) minmax(0,1fr) 96px 96px 60px 220px" }}
      >
        <div className="min-w-0 truncate text-[#D96C4A] font-medium" title={clientName} data-testid={`row-client-${p.id}`}>
          {clientName || "—"}
        </div>
        <div className="min-w-0 truncate text-[#2C2C2A] font-medium" title={p.name} data-testid={`row-name-${p.id}`}>
          {p.name || "—"}
        </div>
        <div className="min-w-0 truncate text-[#4F4F4D]" title={p.location || ""} data-testid={`row-location-${p.id}`}>
          {p.location || "—"}
        </div>
        <div className="text-[#4F4F4D] tabular-nums" data-testid={`row-date-${p.id}`}>
          {formatDateShort(p.date || p.created_at)}
        </div>
        <div data-testid={`row-status-${p.id}`}>
          <Badge className={`${STATUS_BADGE[p.status]} capitalize`}>{STATUS_LABEL[p.status] || p.status}</Badge>
        </div>
        <div data-testid={`row-vat-${p.id}`}>
          <VatBadge rate={p.default_vat_rate} />
        </div>
        <div className="flex items-center justify-end gap-1">
          <OpenProjectButton projectId={p.id} size="sm" testId={`open-project-${p.id}`} />
          <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={onEdit} data-testid={`edit-project-${p.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-[#B84B45]" onClick={onDelete} data-testid={`delete-project-${p.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mobile compacte layout */}
      <div className="md:hidden px-4 py-3 space-y-2" data-testid={`project-row-mobile-${p.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-[#D96C4A] font-medium truncate" title={clientName} data-testid={`row-client-mobile-${p.id}`}>
              {clientName || "—"}
            </div>
            <div className="text-[#2C2C2A] font-semibold truncate" title={p.name} data-testid={`row-name-mobile-${p.id}`}>
              {p.name || "—"}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={onEdit} data-testid={`edit-project-mobile-${p.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-[#B84B45]" onClick={onDelete} data-testid={`delete-project-mobile-${p.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-[#4F4F4D]">
          {p.location && (
            <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{p.location}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            <span className="tabular-nums">{formatDateShort(p.date || p.created_at)}</span>
          </span>
          <Badge className={`${STATUS_BADGE[p.status]} capitalize`}>{STATUS_LABEL[p.status] || p.status}</Badge>
          <VatBadge rate={p.default_vat_rate} />
        </div>
        <OpenProjectButton
          projectId={p.id}
          size="sm"
          testId={`open-project-mobile-${p.id}`}
          className="w-full justify-center"
        />
      </div>
    </li>
  );
}

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  // v6 F10 — land van het bedrijf bepaalt welke BTW-tarieven we tonen
  // (BE: 0/6/21, NL: 0/9/21). Default BE zolang we de profielinfo laden.
  const [companyCountry, setCompanyCountry] = useState("BE");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDel, setConfirmDel] = useState(null);
  // v14ad Fase 1 — Zoek + persistente view/sort per module.
  const [search, setSearch] = useState("");
  const { view, setView, sort, setSort } = useOverviewPreferences("projects", "cards", "created_desc");

  const refresh = async () => {
    const [ps, cs, ts] = await Promise.all([listProjects(), listClients(), listTemplates()]);
    setItems(ps);
    setClients(cs);
    setTemplates(ts);
    // v6 F10 — eenmalig company-country ophalen voor BTW-keuze. Faalt-stil
    // (fallback = BE) zodat de pagina nooit blokkeert op een 401/500.
    try {
      const company = await getCompany();
      if (company?.country) setCompanyCountry(company.country);
    } catch { /* ignore */ }
  };

  // v6 F10 — toon BTW-opties per land. BE: 0/6/21 — NL: 0/9/21.
  const vatOptions = companyCountry === "NL" ? [0, 9, 21] : [0, 6, 21];

  useEffect(() => { refresh().catch(() => {}); }, []);
  // v14ag — Auto-refresh bij terugkeer naar pagina (Optie B). Vangt de flow
  // "gebruiker wijzigt BTW in Offerte Details → keert terug → lijst is stale"
  // op zonder polling of extra backend-logica. Focus + visibilitychange
  // dekken zowel tab-switch als in-app navigatie via de router.
  useEffect(() => {
    const onFocus = () => { refresh().catch(() => {}); };
    const onVisibility = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  useEffect(() => {
    if (params.get("new")) {
      openNew();
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params]); // eslint-disable-line

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  // v14ag — Uitgebreide comparators met alle sorteeropties van de lijst.
  // BTW = pure numeric ascending. Plaats/klant leunen op naturalCompare (nl-BE).
  const PROJECT_COMPARATORS = useMemo(() => ({
    name_asc: (a, b) => naturalCompare(a.name, b.name),
    name_desc: (a, b) => naturalCompare(b.name, a.name),
    client_asc: (a, b) => naturalCompare(clientName(a.client_id), clientName(b.client_id)),
    client_desc: (a, b) => naturalCompare(clientName(b.client_id), clientName(a.client_id)),
    location_asc: (a, b) => naturalCompare(a.location || "", b.location || ""),
    location_desc: (a, b) => naturalCompare(b.location || "", a.location || ""),
    created_desc: (a, b) => String(b.created_at || b.date || "").localeCompare(String(a.created_at || a.date || "")),
    updated_desc: (a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")),
    status_asc: statusOrderComparator((p) => p.status, PROJECT_STATUS_ORDER, "asc"),
    vat_asc: (a, b) => (Number(a.default_vat_rate ?? 0) - Number(b.default_vat_rate ?? 0)),
  }), [clients]);

  const searched = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((p) => {
      const hay = `${p.name || ""} ${clientName(p.client_id) || ""} ${p.location || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, clients]);

  const filtered = useMemo(() => applySort(searched, sort, PROJECT_COMPARATORS), [searched, sort, PROJECT_COMPARATORS]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, client_id: clients[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    // v6 F13 — fallback: oude records met project_type="free" mappen we
    // client-side naar "mixed" zodat de compacte 3-optie selector werkt.
    const safe = { ...p };
    if (safe.project_type === "free") safe.project_type = "mixed";
    setEditing(p); setForm({ ...empty, ...safe }); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Projectnaam is verplicht");
    if (!form.client_id) return toast.error("Selecteer een klant");
    try {
      if (editing) {
        await updateProject(editing.id, form);
        toast.success("Project bijgewerkt");
      } else {
        await createProject(form);
        toast.success("Project aangemaakt");
      }
      setOpen(false);
      refresh();
    } catch {
      toast.error("Fout bij opslaan");
    }
  };

  const remove = async (id) => {
    try {
      await deleteProject(id);
      toast.success("Project verwijderd");
      setConfirmDel(null);
      refresh();
    } catch { toast.error("Fout bij verwijderen"); }
  };

  // v6 F3 — Persisted accordion state per project. Default = collapsed.
  const itemIds = useMemo(() => filtered.map((p) => ({ id: p.id })), [filtered]);
  const {
    isCollapsed: isProjectCollapsed, toggle: toggleProject,
    expandAll: expandAllProjects, collapseAll: collapseAllProjects,
  } = useCollapseMap("emma:listCollapsed:projects", itemIds, true);

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] leading-tight">Projecten</h1>
          <p className="text-[#4F4F4D] mt-1">Koppel projecten aan klanten en start je metingen.</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-3 sm:p-4">
        <OverviewControls
          moduleKey="projects"
          search={{ value: search, onChange: setSearch, placeholder: "Zoek project of klant…", testId: "projects-search" }}
          sort={{ value: sort, onChange: setSort, options: PROJECT_SORT_OPTIONS }}
          view={{ value: view, onChange: setView }}
          actions={
            <Button
              onClick={openNew}
              className="h-11 px-4 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] text-white gap-2 w-full sm:w-auto"
              data-testid="new-project-btn"
              disabled={clients.length === 0}
            >
              <Plus className="w-4 h-4" /> Nieuw project
            </Button>
          }
        />
      </div>

      {clients.length === 0 && (
        <div className="emma-card text-center py-10">
          <p className="text-[#4F4F4D]">Voeg eerst een klant toe voordat je projecten kan aanmaken.</p>
          <Link to="/klanten?new=1" className="inline-block mt-3 text-[#D96C4A] font-semibold hover:underline">
            Naar klanten →
          </Link>
        </div>
      )}

      {/* v6 F3 — bulk toolbar */}
      {filtered.length > 1 && (
        <div className="flex items-center justify-end gap-2 -mb-2" data-testid="projects-bulk-toolbar">
          <button onClick={expandAllProjects} className="text-xs text-[#D96C4A] hover:underline px-2 py-1" data-testid="expand-all-projects">Alles open</button>
          <span className="text-xs text-[#A8A7A0]">·</span>
          <button onClick={collapseAllProjects} className="text-xs text-[#4F4F4D] hover:underline px-2 py-1" data-testid="collapse-all-projects">Alles dicht</button>
        </div>
      )}

      {view === "list" ? (
        <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="projects-list-view">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#4F4F4D]">Geen projecten gevonden.</div>
          ) : (
            <>
              {/* v14ag — Desktop kolomkop (niet klikbaar; sort blijft via select-menu). */}
              <div
                className="hidden md:grid gap-3 px-4 py-2 bg-[#FAF9F6] border-b border-[#E5E2DC] text-[11px] font-semibold uppercase tracking-wide text-[#6E6E6C]"
                style={{ gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1.4fr) minmax(0,1fr) 96px 96px 60px 220px" }}
                data-testid="projects-list-header"
              >
                <div>Klant</div>
                <div>Project</div>
                <div>Plaats</div>
                <div>Datum</div>
                <div>Status</div>
                <div>BTW</div>
                <div className="text-right">Acties</div>
              </div>
              <ul className="divide-y divide-[#E5E2DC]">
                {filtered.map((p) => (
                  <ProjectListRow
                    key={p.id}
                    project={p}
                    clientName={clientName(p.client_id)}
                    onEdit={() => openEdit(p)}
                    onDelete={() => setConfirmDel(p)}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="projects-grid">
        {filtered.map((p) => {
          const collapsed = isProjectCollapsed(p.id);
          return (
          <div key={p.id} className="emma-card group" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start gap-2">
              <button
                type="button"
                onClick={() => toggleProject(p.id)}
                className="w-8 h-8 -m-1 rounded-lg hover:bg-[#FAF9F6] flex items-center justify-center shrink-0"
                data-testid={`project-toggle-${p.id}`}
                aria-expanded={!collapsed}
                aria-label={collapsed ? "Details tonen" : "Details verbergen"}
              >
                {collapsed
                  ? <ChevronRight className="w-4 h-4 text-[#5D7259]" />
                  : <ChevronDown className="w-4 h-4 text-[#5D7259]" />}
              </button>
              <div className="min-w-0 flex-1">
                {/* v14af — Actierij: statusbadge + Open project → naast elkaar.
                    Open project blijft altijd zichtbaar zonder de card hoger
                    te maken dan de originele ingeklapte versie. */}
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Badge className={`${STATUS_BADGE[p.status]} capitalize`}>{STATUS_LABEL[p.status] || p.status}</Badge>
                  <OpenProjectButton projectId={p.id} size="sm" testId={`open-project-${p.id}`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#2C2C2A] truncate">{p.name}</h3>
                <p className="text-sm text-[#D96C4A] font-medium mt-0.5 truncate">{clientName(p.client_id)}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(p)} className="w-9 h-9 rounded-lg hover:bg-[#F0EFEA] flex items-center justify-center" data-testid={`edit-project-${p.id}`}>
                  <Pencil className="w-4 h-4 text-[#4F4F4D]" />
                </button>
                <button onClick={() => setConfirmDel(p)} className="w-9 h-9 rounded-lg hover:bg-red-50 flex items-center justify-center" data-testid={`delete-project-${p.id}`}>
                  <Trash2 className="w-4 h-4 text-[#D9534F]" />
                </button>
              </div>
            </div>
            {!collapsed && (
              <div className="space-y-1.5 mt-3 text-sm text-[#4F4F4D]">
                {p.location && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{p.location}</div>}
                {p.date && <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{p.date}</div>}
                {p.notes && <p className="text-sm text-[#4F4F4D] mt-2 line-clamp-2 border-t border-[#E5E2DC] pt-2">{p.notes}</p>}
              </div>
            )}
          </div>
          );
        })}
        {items.length === 0 && clients.length > 0 && (
          <div className="emma-card md:col-span-2 xl:col-span-3 text-center text-[#4F4F4D] py-12">
            Nog geen projecten. Klik op <strong>Nieuw project</strong> om te starten.
          </div>
        )}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg w-[calc(100vw-2rem)] sm:w-full p-0 max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col gap-0 overflow-hidden"
          data-testid="project-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E5E2DC]">
            <DialogTitle className="font-heading">{editing ? "Project bewerken" : "Nieuw project"}</DialogTitle>
            <DialogDescription>Koppel dit project aan een klant.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4" data-testid="project-dialog-body">
            <div>
              <Label>Klant *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="h-14 mt-1.5 rounded-xl" data-testid="project-client-select">
                  <SelectValue placeholder="Selecteer klant" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Projectnaam *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bv. Renovatie woning Janssens"
                className="h-14 mt-1.5 rounded-xl" data-testid="project-name-input" />
              <p className="text-xs text-[#6E6E6C] mt-1">
                Korte interne naam voor je projectoverzicht. Verschijnt op de PDF als er geen aparte Offertetitel is ingesteld.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Locatie</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="h-14 mt-1.5 rounded-xl" data-testid="project-location-input" />
              </div>
              <div>
                <Label>Datum</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="h-14 mt-1.5 rounded-xl" data-testid="project-date-input" />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              {/* v14ak — Manuele status keuze + auto-follow toggle. Een manueel
                  gekozen status zet override AAN; de toggle schakelt hem uit
                  en synchroniseert onmiddellijk met de huidige quote_status. */}
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v, status_manual_override: true })}
              >
                <SelectTrigger className="h-14 mt-1.5 rounded-xl" data-testid="project-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="concept">Concept</SelectItem>
                  <SelectItem value="waiting">Afwachting</SelectItem>
                  <SelectItem value="active">Actief</SelectItem>
                  <SelectItem value="rejected">Afgewezen</SelectItem>
                  <SelectItem value="delivered">Opgeleverd</SelectItem>
                </SelectContent>
              </Select>
              <label className="mt-2 flex items-center gap-2 cursor-pointer select-none" data-testid="project-status-auto-toggle-wrapper">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-[#D96C4A]"
                  checked={!form.status_manual_override}
                  onChange={(e) => setForm({ ...form, status_manual_override: !e.target.checked })}
                  data-testid="project-status-auto-toggle"
                />
                <span className="text-xs text-[#4F4F4D]">
                  Status automatisch bepalen op basis van offerte
                </span>
              </label>
              <p className="text-[11px] text-[#A8A7A0] mt-1">
                {form.status_manual_override
                  ? "Manueel gekozen — automatische sync gepauzeerd."
                  : "Automatisch volgen actief — status volgt de offertestatus."}
              </p>
            </div>
            {/* v14ak-fix — "Offerte status" select bewust verwijderd uit deze modal.
                Offertestatus wordt beheerd op de Offertes-pagina; hier tonen we
                enkel de projectstatus (met optionele auto-sync). */}
            <div>
              <Label>BTW-tarief project</Label>
              <Select value={String(form.default_vat_rate ?? 21)} onValueChange={(v) => setForm({ ...form, default_vat_rate: parseInt(v) })}>
                <SelectTrigger className="h-14 mt-1.5 rounded-xl" data-testid="project-vat-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {vatOptions.map((rate) => (
                    <SelectItem key={rate} value={String(rate)} data-testid={`project-vat-option-${rate}`}>{rate}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Notities</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3} className="mt-1.5 rounded-xl" data-testid="project-notes-input" />
            </div>
            {/* v14ad — Projecttype-chips uit UI verwijderd (backward-compat:
                form.project_type wordt nog steeds naar backend gestuurd met
                de default "mixed" waarde uit `empty`). Geen inhoudelijke
                impact op ruimtes, zones, offertes of BTW-logica. */}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-[#E5E2DC] bg-white flex-row gap-2 sm:flex-row" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12 rounded-xl flex-1 sm:flex-none" data-testid="project-cancel-btn">Annuleer</Button>
            <Button onClick={save} className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] flex-1 sm:flex-none" data-testid="project-save-btn">
              {editing ? "Bijwerken" : "Opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Project verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDel?.name}" en alle bijhorende offertes worden verwijderd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleer</AlertDialogCancel>
            <AlertDialogAction className="bg-[#D9534F] hover:bg-[#B23A3A]" onClick={() => remove(confirmDel.id)}>
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
